import {
  Badge,
  Button,
  ContextMenu,
  ContextMenuItem,
  DataTable,
  DataTableColumnHeader,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { MatterPhaseProjectLifecycle, PhaseProject } from "@/lib/types";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import {
  Archive,
  CirclePlay,
  Eye,
  Pencil,
  RotateCcw,
  Trash2,
} from "lucide-react";
import pluralize from "pluralize";

export type PhaseProjectLifecycleAction =
  | "activate"
  | "archive"
  | "reactivate"
  | "delete";

const lifecycleBadgeClass: Record<MatterPhaseProjectLifecycle, string> = {
  Draft: "border-amber-200 bg-amber-50 text-amber-800",
  Active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  Archived: "border-slate-200 bg-slate-100 text-slate-700",
};

export function PhaseProjectTable({
  projects,
  incidentTypeLabels,
  canUpdate,
  canDelete,
  isFetching,
  pagination,
  pageCount,
  rowCount,
  onPaginationChange,
  onOpen,
  onEdit,
  onLifecycleAction,
}: {
  projects: PhaseProject[];
  incidentTypeLabels: Map<string, string>;
  canUpdate: boolean;
  canDelete: boolean;
  isFetching: boolean;
  pagination: PaginationState;
  pageCount: number;
  rowCount: number;
  onPaginationChange: (pagination: PaginationState) => void;
  onOpen: (project: PhaseProject) => void;
  onEdit: (project: PhaseProject) => void;
  onLifecycleAction: (
    type: PhaseProjectLifecycleAction,
    project: PhaseProject,
  ) => void;
}) {
  const columns: ColumnDef<PhaseProject>[] = [
    {
      id: "Project",
      accessorFn: (project) => project.title,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left font-medium"
          onClick={(event) => {
            event.stopPropagation();
            onOpen(row.original);
          }}
        >
          {row.original.title}
        </button>
      ),
    },
    {
      id: "Status",
      accessorFn: (project) => project.lifecycle,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      enableSorting: false,
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={lifecycleBadgeClass[row.original.lifecycle]}
        >
          {row.original.lifecycle}
        </Badge>
      ),
    },
    {
      id: "Incident Types",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      enableSorting: false,
      cell: ({ row }) => {
        const labels = row.original.currentRevision.incidentTypeIds.map(
          (id) => incidentTypeLabels.get(id) ?? "Unavailable Incident Type",
        );
        return (
          <span
            className="text-muted-foreground block max-w-80 truncate"
            title={labels.join(", ")}
          >
            {labels.length > 2
              ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`
              : labels.join(", ") || "—"}
          </span>
        );
      },
    },
    {
      id: "Contents",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      enableSorting: false,
      cell: ({ row }) => {
        const phases = row.original.currentRevision.phases;
        const actionCount = phases.reduce(
          (total, phase) => total + phase.actions.length,
          0,
        );
        const widgetCount = phases.reduce(
          (total, phase) => total + (phase.widgets?.length ?? 0),
          0,
        );
        return (
          <span className="text-muted-foreground whitespace-nowrap">
            {phases.length} {pluralize("Phase", phases.length)} · {actionCount}{" "}
            {pluralize("Action", actionCount)} · {widgetCount}{" "}
            {pluralize("Widget", widgetCount)}
          </span>
        );
      },
    },
    {
      id: "Actions",
      header: "",
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) => {
        const project = row.original;
        return (
          <div
            className="flex justify-end gap-1"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {canUpdate && project.lifecycle === "Draft" ? (
              <TooltipWrapper message="Activate phase project">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Activate ${project.title}`}
                  onClick={() => {
                    onLifecycleAction("activate", project);
                  }}
                >
                  <CirclePlay className="h-4 w-4" />
                </Button>
              </TooltipWrapper>
            ) : null}
            {canUpdate && project.lifecycle === "Active" ? (
              <TooltipWrapper message="Archive phase project">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Archive ${project.title}`}
                  onClick={() => {
                    onLifecycleAction("archive", project);
                  }}
                >
                  <Archive className="h-4 w-4" />
                </Button>
              </TooltipWrapper>
            ) : null}
            {canUpdate && project.lifecycle === "Archived" ? (
              <TooltipWrapper message="Reactivate phase project">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Reactivate ${project.title}`}
                  onClick={() => {
                    onLifecycleAction("reactivate", project);
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </TooltipWrapper>
            ) : null}
            {canDelete && project.lifecycle === "Draft" ? (
              <TooltipWrapper message="Delete phase project">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${project.title}`}
                  onClick={() => {
                    onLifecycleAction("delete", project);
                  }}
                >
                  <Trash2 className="text-destructive h-4 w-4" />
                </Button>
              </TooltipWrapper>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      tableName={tanstackTableNames.phaseProjects}
      allowLocalStorage={false}
      columns={columns}
      data={projects}
      noResultsMessage="No Phase Projects found."
      getRowId={(project) => project.id}
      onRowClick={(row) => {
        onOpen(row.original);
      }}
      renderRowContextMenu={(row, trigger) => (
        <ContextMenu entityName="Phase Project" trigger={trigger}>
          <ContextMenuItem
            onSelect={() => {
              onOpen(row.original);
            }}
          >
            <Eye className="size-4" aria-hidden="true" />
            View project
          </ContextMenuItem>
          {canUpdate && row.original.lifecycle !== "Archived" ? (
            <ContextMenuItem
              onSelect={() => {
                onEdit(row.original);
              }}
            >
              <Pencil className="size-4" aria-hidden="true" />
              Edit project
            </ContextMenuItem>
          ) : null}
        </ContextMenu>
      )}
      isFetching={isFetching}
      hideSearchBar
      hideVisiibilityDropdown
      serverPagination={{
        pagination,
        pageCount,
        rowCount,
        onPaginationChange: (updater) => {
          onPaginationChange(
            typeof updater === "function" ? updater(pagination) : updater,
          );
        },
      }}
    />
  );
}
